import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CandlestickSeries,
  HistogramSeries,
  LineSeries,
  createChart,
  createSeriesMarkers,
  type IChartApi,
  type ISeriesApi,
  type MouseEventParams,
  type ISeriesMarkersPluginApi,
  type SeriesMarker,
  type UTCTimestamp,
  type Time,
} from 'lightweight-charts';
import { formatPriceInt, getChartTheme, type ChartTheme, type ThemeName } from '../../lib/chartTheme';
import { PriceScaleControls } from './PriceScaleControls';
import type { MaPeriod, PaneId } from '../../lib/app';
import type { Bar, MinuteDay, MinuteResponse } from '../../lib/api';
import type { ValuationKind } from '../../lib/app';
import {
  sma, bollinger, bbi, macd, rsi, kdj, volMa,
  averageLine, axisTime, axisBase, dayStride, offsetToHHMM, minutesAfterToClose, sessionCloseHHMM, decodeAxisTime, type SessionMarket,
} from '../../lib/indicators';

/**
 * 把某日序列尾部用 **逐分钟 whitespace 锚点** 补到收盘。
 *
 * 为什么逐分钟而不是 30 分钟格点：库对数据项**等宽排布**（每项一宽度，
 * 与项间时间差无关）—— 若未来时段只挂格点，14:30→15:00 的 30 分钟会被
 * 压成一项宽、15:00 刻度挤进 14:30、全日比例错乱（实测所见）。
 *
 * 为什么必须锚点：库的 timeToCoordinate 只解析 **series 里已存在**的时间点
 * —— 盘中未走完的未来刻度（13:30…15:00）若不挂锚点拿 null，
 * 自绘格线消失 = 「剩余交易时间显示不出来」（用户所见）。
 *
 * 锚点无 value 不画线；已收盘日（末点=收盘 → 空清单）零追加；
 * lastHHMM 传当日真实末点，生成的键全部大于末键，无重复风险；
 * dayData 由调用方每次重建（dedupe 后），轮询重复执行天然幂等。
 */
function fillToClose(
  dayData: { key: number; value: number }[],
  lastHHMM: string,
  base: number, dayIndex: number, market: SessionMarket,
): void {
  for (const h of minutesAfterToClose(lastHHMM, market)) {
    dayData.push({ key: axisTime(base, dayIndex, h, market), value: NaN });
  }
}

/** 由「当日内压缩偏移」反推最后一个真实分钟（容忍 fill 幂等重跑） */
function lastMinOfDay(dayData: { key: number }[], base: number, dayIndex: number, market: SessionMarket): string {
  const rel = dayData[dayData.length - 1]!.key - base - dayIndex * dayStride(market);
  return offsetToHHMM(rel, market);
}

import { dedupeByTime } from '../../lib/chartDedup';
import { clampTimeScale } from './nav';
import { MinuteTimeAxis } from './MinuteTimeAxis';
import { NoteLane, type NoteDay, type NoteDot } from './noteLane';

/**
 * 有预测的一天（一天一条），按日期升序；情绪为空的预测上游已过滤。
 * sentiment 不放在 NoteDot —— 归并成柱后才需要把它换算成点色与文案。
 */
export interface PredDay {
  date: string;
  sentiment: 'bullish' | 'bearish' | 'neutral';
}

/**
 * 图表渲染（Phase 6）
 *
 * 用 lightweight-charts 的 **v5 多 pane API**（`addSeries(Series, paneIndex)`）。
 * v4 的 `addCandlestickSeries()` 只能挂在主图，副图需自行实现画布 —— v5 简化了这点。
 *
 * 关键约束：
 *  · 主题切换必须重建图表：库不读 CSS 变量（见 chartTheme.ts 说明）
 *  · 容器尺寸变化靠 `autoSize`，不手动监听 resize（避免与外部的 ResizeObserver 打架）
 *  · 指标序列在**数据变化时**重算，不在渲染中算（渲染中算会导致每次 tick 都重算指标）
 */

export interface ChartProps {
  bars: Bar[];
  /** 分时（单日）：与 bars 二选一 */
  minute?: MinuteResponse | null;
  /** 5 日分时的多日数据；period === 'MIN5' 时使用 */
  minuteDays?: MinuteDay[] | null;
  /** 交易市场：决定分时的上午收盘与午休压缩（CN / HK） */
  market?: SessionMarket;
  period: string;
  theme: ThemeName;
  overlays: Set<string>;
  /** 要画哪几条 MA；为空则不画（用户在 ⚙ 里全部取消） */
  maPeriods: MaPeriod[];
  panes: (PaneId | null)[];
  /** 昨收价：分时的涨跌参照系，同时决定分时纵轴的对称范围 */
  prevClose?: number | null;
  /** 分时纵轴的对称半幅（相对昨收的比例，如 0.015 = ±1.5%）；缺省按波动自适应 */
  minuteSpanPct?: number;
  /**
   * 估值叠加：{ date, pe, divYield }[]。
   * 只有 20 个交易日（需求 §3.3），故**独立右轴**并标注单位 ——
   * 与价格共轴会完全压扁曲线。
   */
  valuation?: { date: string; pe1?: number; pe2?: number; divYield2?: number }[];
  /** 估值口径：pe1=静态 / pe2=TTM（需求 F2.5） */
  valuationBasis?: 'pe1' | 'pe2';
  valuationKind?: ValuationKind;
  /** 图例就绪回调（图表建好后触发一次） */
  onReady?: () => void;
  /** 双击绘图区切换图表高度档位（作用域仅绘图区，避免读数条/头部误触） */
  onCyclePreset?: () => void;
  /**
   * 十字线悬停到某根柱时上报其日期；离开图表上报 null。
   *
   * 读数条据此显示该柱的 OHLC —— 改前只显示最新价，光标移动时数字不动。
   * 上报「日期」而非 seriesData：估值曲线用了独立 priceScaleId，
   * crosshair.mode:Normal 在多轴下可能取不到主序列标签，由调用方按日期查更可靠。
   */
  onCrosshairBar?: (date: string | null) => void;
  /**
   * 点击某根柱 → 上报该柱日期（需求「点柱即查看当日笔记与新闻」）。
   *
   * 再次点击**同一根**柱时上报 null，表示"取消选中、回到最新交易日" ——
   * 否则选中后没有任何退路，用户只能去点筛选器的「最新」。
   */
  onSelectDate?: (date: string | null) => void;
  /** 当前被选中的柱（打标记用）；null = 无选中 */
  selectedDate?: string | null;
  /** 有日记的日期与篇数（升序），用于在主图上边界内侧浮动小圆点 */
  noteDates?: NoteDay[];
  /** 有预测（情绪已设）的日期（升序），同笔记机制、在其行下方的第二行 */
  predDates?: PredDay[];
  /**
   * 分时周期下十字线悬停的分钟点（dayIndex+hhmm；null=离开）。
   * 头部「点位/涨跌幅」跟随光标；K线走 onCrosshairBar 通道，分时走这里。
   */
  onCrosshairMinute?: OnCrosshairMinute;
}

/** 分时十字线悬停点回调形态。 */
export type OnCrosshairMinute = (pt: { dayIndex: number; hhmm: string } | null) => void;

type AnySeries = ISeriesApi<never> | ISeriesApi<'Candlestick'> | ISeriesApi<'Line'> | ISeriesApi<'Histogram'>;

/**
 * `applyOptions` 的类型是 `never`（库把各 series 类型的 options 收窄到联合类型，
 * 直接传会报错）。这里只在运行期按对象形状使用，故收口为宽松签名。
 */
function setAutoscale(
  series: ISeriesApi<'Line'>,
  min: number,
  max: number,
): void {
  (series as unknown as {
    applyOptions(o: {
      autoscaleInfoProvider: () => { priceRange: { minValue: number; maxValue: number } };
    }): void;
  }).applyOptions({
    autoscaleInfoProvider: () => ({
      priceRange: { minValue: min, maxValue: max },
    }),
  });
}

/**
 * 5 日分时渲染：**真实多日时间轴、绝对价格、从左到右**（docs/UI设计.md §3.2）。
 *
 * 每天一条独立 `LineSeries`，横轴用各日合成坐标（午休/隔夜压缩），数据域不满时
 * 会把已发生数据拉伸铺满右端、剩余交易时段消失 —— 修法：最后一日的序列尾部
 * 挂**逐分钟 whitespace 锚点**（fillToClose），把数据域撑到
 * 「全 5 日」终点；视窗随 domain 精确对齐 09:30/day1 → 收盘/dayN：
 * 等宽格子自然恒定，末日右侧按真实时间比例留白。
 * appliedRangeKey 保证只在日期集变化时定一次视窗，盘中 60s 轮询只 setData、
 * 不重置用户手动缩放/拖拽。
 */
function renderFiveDaySeries(
  chart: IChartApi,
  t: ChartTheme,
  ref: { current: AnySeries[] },
  days: MinuteDay[] | null | undefined,
  market: SessionMarket,
  rangeKey: string,
  appliedRangeKey: { current: string | null },
): void {
  for (const s of ref.current) {
    try { chart.removeSeries(s as never); } catch { /* 图表已销毁 */ }
  }
  ref.current = [];

  const valid = (days ?? []).filter((d) => d.points.length > 0);
  if (valid.length === 0) return;

  // 锚点取首个交易日（与自绘轴 axisBase(axisDates[0]) 一致），每天一块等宽会话
  const base = axisBase(valid[0]!.date);
  valid.forEach((d, i) => {
    const s = chart.addSeries(LineSeries, {
      color: t.band[i % t.band.length],
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
      // 右侧标尺整数(与其他图表统一)—— 此前默认 precision 2 显示 3875.00
      priceFormat: { type: 'custom', formatter: formatPriceInt, minMove: 0.01 },
    });
    const dayData = dedupeByTime(
      d.points.map((p) => ({
        key: axisTime(base, i, p.time, market),
        value: p.price,
      })),
    );
    if (i === valid.length - 1) {
      // 最后一日（可能是进行中的今天）：逐分钟锚点撑满该日会话
      fillToClose(dayData, lastMinOfDay(dayData, base, i, market), base, i, market);
    }
    s.setData(dayData.map((x) => (
      Number.isFinite(x.value)
        ? { time: x.key as UTCTimestamp, value: x.value }
        : { time: x.key as UTCTimestamp } // whitespace（无 value，不画线）
    )));
    ref.current.push(s);
  });

  if (appliedRangeKey.current !== rangeKey) {
    appliedRangeKey.current = rangeKey;
    // 视窗 = 全 5 日 × 全日压缩跨度。两端（首日 09:30 / 末日收盘）都有
    // 逐分钟锚点保证坐标可解析，所以 setVisibleRange 能精确贴到两侧；
    // 仅在日期集变化时设定，盘中轮询不干扰用户缩放/拖拽。
    const endAxis = axisTime(
      base, valid.length - 1, sessionCloseHHMM(market), market,
    );
    chart.timeScale().setVisibleRange({
      from: base as UTCTimestamp,
      // 同 MIN1:右端 +8 分钟,防收盘标签贴边挤出
      to: (endAxis + 8 * 60) as UTCTimestamp,
    });
  }
}

export function ChartCanvas({
  bars, minute, minuteDays, market = 'CN', period, theme, overlays, maPeriods, panes, height, prevClose,
minuteSpanPct, valuation, valuationBasis = 'pe2', valuationKind = 'pe', onReady: onReadyProp,
  onCrosshairBar, onSelectDate, selectedDate, noteDates = [], predDates = [], onCyclePreset,
  onCrosshairMinute,
}: ChartProps & { height: number }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  /** 暴露给自绘时间轴的实例（chartRef 变化不触发渲染） */
  const [chartApi, setChartApi] = useState<IChartApi | null>(null);
  const seriesRef = useRef<AnySeries[]>([]);
  /** 记录每根副图占据的 pane index，销毁时按索引移除 */
  const paneSeriesRef = useRef<{ pane: number; series: AnySeries }[]>([]);
  /**
   * 主图叠加序列（MA/BOLL/BBI/估值）。
   *
   * ⚠️ 改为**命令式**管理（而非 effect 的 cleanup）：
   *    cleanup 会在依赖变化时无条件移除序列，而 `bars` 在加载期是**每帧都变的新空数组**，
   *    于是「切换指数 → 加载中 → 叠加/副图全被拆掉、主图却仍留着旧蜡烛」，
   *    视觉上就是「主副图内容时有时无」。改为只在**拿到数据要重建时**才清除，
   *    无数据则整体保留上一轮，与主图行为一致。
   */
  const overlaySeriesRef = useRef<AnySeries[]>([]);
  /** 5 日分时的水平带序列（每天一条） */
  const bandSeriesRef = useRef<AnySeries[]>([]);
  /* 分时视窗守卫：记录「已设定固定区间」的数据范围键。盘中 60s 轮询会
     反复触发灌数据 effect，同键不重设 timeScale —— 否则用户手动缩放/拖拽
     每分钟被弹回。键取日期集：切日期/切数据范围才重设。 */
  const min1RangeKeyRef = useRef<string | null>(null);
  const fiveDayRangeKeyRef = useRef<string | null>(null);
  /**
   * 主图上的价线（昨收）。
   * ⚠️ lightweight-charts 的 createPriceLine **没有返回值、无法查找**，
   *    只能持有 remove()。若每次灌数据都新建而不移除，切一次指数就会
   *    永久多出一条线 —— 实测表现为纵轴被旧指数的昨收撑开（8128 与 3842 同框）。
   */
  const prevCloseLineRef = useRef<ReturnType<ISeriesApi<'Line'>['createPriceLine']> | null>(null);
  const onReadyRef = useRef(onReadyProp);
  onReadyRef.current = onReadyProp;
  // 用 ref 转发回调，避免订阅 effect 因回调身份变化而反复重订阅
  const onCrosshairBarRef = useRef(onCrosshairBar);
  onCrosshairBarRef.current = onCrosshairBar;
  /** 上次上报的日期，用于去重（避免每次 mousemove 都 setState） */
  const lastDateRef = useRef<string | null>(null);
  const onSelectDateRef = useRef(onSelectDate);
  onSelectDateRef.current = onSelectDate;
  const onCrosshairMinuteRef = useRef(onCrosshairMinute);
  onCrosshairMinuteRef.current = onCrosshairMinute;
  const selectedDateRef = useRef<string | null>(selectedDate ?? null);
  selectedDateRef.current = selectedDate ?? null;
  /** createSeriesMarkers 返回的句柄：换数据要重设标记，故持有以便 clearMarkers */
  const markersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);

  /** 自绘时间轴所需的日期：单日取 minute.date，5 日取各带日期（过滤空日） */
  const axisDates = useMemo(() => {
    if (period === 'MIN1') return minute?.date ? [minute.date] : [];
    if (period === 'MIN5') return (minuteDays ?? []).filter((d) => d.points.length > 0).map((d) => d.date);
    return [];
  }, [period, minute?.date, minuteDays]);

  /**
   * 合成轴映射（供十字线格式化器读取）。时间轴不是真实日期，故用 ref 传入：
   * `localization.timeFormatter` 是建图时的闭包，无法直接读随数据变化的日期。
   */
  const axisRef = useRef<{ base: number; dates: string[]; multi: boolean; market: SessionMarket } | null>(null);
  axisRef.current = axisDates.length > 0
    ? { base: axisBase(axisDates[0]!), dates: axisDates, multi: period === 'MIN5', market }
    : null;

  /* ── 建/重建图表：主题、周期形态变化时重建 ── */
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const t = getChartTheme(theme);
    const isMin1 = period === 'MIN1';
    const isMin5 = period === 'MIN5';

    // 新 chart 实例有新的 timeScale —— 区间守卫必须重新应用一次
    min1RangeKeyRef.current = null;
    fiveDayRangeKeyRef.current = null;

    const chart = createChart(el, {
      autoSize: true,
      layout: {
        background: { color: t.background },
        textColor: t.text2,
        fontSize: 11,
        fontFamily: 'inherit',
        attributionLogo: false,
      },
      grid: {
        // 分时的竖网由自绘层（MinuteTimeAxis）按 30 分钟格接管 ——
        // 库按**每个数据项**画竖线，分钟序列下是每分钟一条的密网变脏
        vertLines: { color: t.grid, visible: !(isMin1 || isMin5) },
        horzLines: { color: t.grid },
      },
      rightPriceScale: { borderColor: t.border, scaleMargins: { top: 0.1, bottom: 0.1 } },
      timeScale: {
        // 分时视窗右缘=收盘锚点,右偏移会把 15:00 顶出屏外 → 置 0;
        // K线保留 4 根「呼吸」留白
        borderColor: t.border, rightOffset: (isMin1 || isMin5) ? 0 : 4, barSpacing: 7,
        timeVisible: isMin1 || isMin5,
        secondsVisible: false,
        // 刻度线与标签交由自绘层（午休压缩后自动刻度落点不精确）
        ticksVisible: !(isMin1 || isMin5),
        tickMarkFormatter: isMin1 || isMin5 ? () => '' : undefined,
      },
      crosshair: {
        mode: 0,
        vertLine: {
          color: t.crosshair, width: 1, style: 2, labelBackgroundColor: t.text2,
          // 分时的时间标签改由自绘浮动徽标显示（库的小标签在压缩轴上易被刻度淹没）
          labelVisible: !(isMin1 || isMin5),
        },
        horzLine: { color: t.crosshair, width: 1, style: 2, labelBackgroundColor: t.text2 },
      },
      localization: {
        locale: 'zh-CN',
        // 自绘时间轴负责底部刻度，库只保留十字线标签 → 把合成坐标还原成真实日期/时间
        timeFormatter: (time: Time) => {
          if (!isMin1 && !isMin5) return String(time);
          const a = axisRef.current;
          if (!a) return String(time);
          const { dayIndex, hhmm } = decodeAxisTime(Number(time), a.base, a.market);
          const date = a.dates[dayIndex];
          return a.multi && date ? `${date.slice(5)} ${hhmm}` : hhmm;
        },
      },
    });
    chartRef.current = chart;
    // DEV-only 探针:浏览器控制台直接读取/操作 timeScale,排查视窗问题用
    if (import.meta.env.DEV) {
      (window as unknown as { __snChart?: IChartApi }).__snChart = chart;
    }
    setChartApi(chart);
    seriesRef.current = [];
    paneSeriesRef.current = [];
    overlaySeriesRef.current = [];
    bandSeriesRef.current = [];
    prevCloseLineRef.current = null;

    /* 主图：单日分时用折线（价+均价），5 日分时在数据 effect 里按天数建带，K 线用蜡烛 */
    if (isMin1) {
      const s = chart.addSeries(LineSeries, {
        color: t.minuteLine, lineWidth: 1, priceLineVisible: false, lastValueVisible: true,
        // 分时同样去尾零：否则右侧纵轴显示 3810.00，而 K 线已去零，两种形态不一致
        priceFormat: { type: 'custom', formatter: formatPriceInt, minMove: 0.01 },
      });
      seriesRef.current.push(s);
      const avg = chart.addSeries(LineSeries, {
        color: t.averageLine, lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
        // 与主价格线同一右轴:不一致的精度会把标尺拉回两位小数
        priceFormat: { type: 'custom', formatter: formatPriceInt, minMove: 0.01 },
      });
      seriesRef.current.push(avg);
    } else if (!isMin5) {
      const s = chart.addSeries(CandlestickSeries, {
        upColor: t.up, downColor: t.down,
        borderUpColor: t.up, borderDownColor: t.down,
        wickUpColor: t.up, wickDownColor: t.down,
        priceLineVisible: true, lastValueVisible: true,
        // 纵轴刻度与十字线价格标签共用（见 formatPriceInt 的说明，整数标尺),
        // 去掉 `3810.00` 的尾部零，白占的宽度还给绘图区
        priceFormat: { type: 'custom', formatter: formatPriceInt, minMove: 0.01 },
      });
      seriesRef.current.push(s);
    }
    /**
     * 十字线悬停 → 上报当前柱的日期，供读数条显示 OHLC（仅 K线周期）。
     * 分时的合成坐标走 OnCrosshairMinute 通道（见注册处注释）。
     */
    const isMinute = period === 'MIN1' || period === 'MIN5';
    const onCross = (p: MouseEventParams) => {
      if (isMinute) return;
      const t = p.time;
      // K线的 time 是 business-day 字符串（'2026-10-09'），直接就是日期
      const date = typeof t === 'string' ? t : null;
      const last = onCrosshairBarRef.current;
      // 仅在变化时上报，避免高频 mousemove 引发无谓的 setState
      if (lastDateRef.current !== date) {
        lastDateRef.current = date;
        last?.(date);
      }
    };
    const onCrossLeave = () => {
      if (lastDateRef.current === null) return;
      lastDateRef.current = null;
      onCrosshairBarRef.current?.(null);
    };
    /** 分时专用:悬停分钟点上报(null=离开)。仅 isMinute 时注册。 */
    const onCrossMinute = (p: MouseEventParams) => {
      const cb = onCrosshairMinuteRef.current;
      if (!cb) return;
      const a = axisRef.current;
      if (!a || p.time === undefined) { cb(null); return; }
      const { dayIndex, hhmm } = decodeAxisTime(Number(p.time), a.base, a.market);
      if (dayIndex < 0 || dayIndex >= a.dates.length) { cb(null); return; }
      cb({ dayIndex, hhmm });
    };
    const onMinuteLeave = () => onCrosshairMinuteRef.current?.(null);
    /**
     * 点击柱 → 选中该日。K线的 time 即 business-day 字符串，直接是日期。
     * 分时周期 time 是合成 epoch 坐标，此时不响应 —— 否则会把合成坐标当成日期。
     */
    const onClick = (p: MouseEventParams) => {
      if (isMinute) return;
      const t = p.time;
      if (typeof t !== 'string') return;
      // 再点同一根 = 取消选中回到最新
      onSelectDateRef.current?.(selectedDateRef.current === t ? null : t);
    };
    if (!isMinute) {
      chart.subscribeCrosshairMove(onCross);
      chart.subscribeClick(onClick);
      // 指针移出图表时清空读数条（库不保证再回调一次 crosshairMove）
      el.addEventListener('mouseleave', onCrossLeave);
    } else {
      // 分时:_decodeAxisTime 还原 (dayIndex, hhmm);落点在合法会话之外上报 null
      chart.subscribeCrosshairMove(onCrossMinute);
      el.addEventListener('mouseleave', onMinuteLeave);
    }

    // 两侧边界：拖拽/滚轮缩放不得越界（左=首根柱贴左缘，右=末柱停在实时位置）。
    // 判定与按钮共用 nav.ts，迟滞阈值内不自激。
    const ts0 = chart.timeScale();
    const onRangeChange = () => { clampTimeScale(ts0); };
    ts0.subscribeVisibleLogicalRangeChange(onRangeChange);

    onReadyRef.current?.();
    return () => {
      if (!isMinute) {
        chart.unsubscribeCrosshairMove(onCross);
        chart.unsubscribeClick(onClick);
      } else {
        chart.unsubscribeCrosshairMove(onCrossMinute);
        el.removeEventListener('mouseleave', onMinuteLeave);
      }
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(onRangeChange);
      markersRef.current?.detach();
      // 必须置 null：下一张图表的标记 effect 以 `if (!markersRef.current)` 决定
      // 是否新建原语，ref 若残留已 detach 的旧原语，新图表就永远拿不到标记
      // （StrictMode 双调用下表现为「白点从未出现过」；生产态表现为切周期后失效）。
      markersRef.current = null;
      el.removeEventListener('mouseleave', onCrossLeave);
      onCrosshairBarRef.current?.(null);
      chart.remove();
      chartRef.current = null;
      setChartApi(null);
      seriesRef.current = [];
      paneSeriesRef.current = [];
      overlaySeriesRef.current = [];
      bandSeriesRef.current = [];
      prevCloseLineRef.current = null;
    };
  }, [theme, period, market]);

  /* ── 灌数据 ── */
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const t = getChartTheme(theme);

    // 5 日分时：按天数建真实多日绝对价格线，与主图 seriesRef 无关
    if (period === 'MIN5') {
      renderFiveDaySeries(chart, t, bandSeriesRef, minuteDays, market,
        minuteDays?.map((d) => d.date).join(',') ?? '', fiveDayRangeKeyRef);
      return;
    }

    const main = seriesRef.current[0];
    if (!main) return;

    if (period === 'MIN1') {
      const avg = seriesRef.current[1];
      const pts = minute?.points ?? [];
      const date = minute?.date ?? '';
      if (pts.length === 0 || !date) return;

      /**
       * ⚠️ lightweight-charts 硬性要求 data **按 time 严格升序**且无重复。
       * 上游 tick 偶发乱序/重复（实测腾讯 day/query 会给出非单调序列），
       * 直接 setData 会触发库内部断言 `data must be asc ordered by time` 并炸掉整页。
       * 故灌数据前必须按 epoch 时间键归一化。
       */
      const base = axisBase(date);
      const line = dedupeByTime(
        pts.map((p) => ({ key: axisTime(base, 0, p.time, market), value: p.price })),
      );
      /**
       * 格点 whitespace 锚点：把数据域撑到**全日会话**（09:30 → 收盘）。
       * 盘中进行时数据未走完，fitContent 以数据首尾定边界会把已发生部分
       * 拉伸铺满右端、剩余交易时段整段消失；锚点后曲线按真实时间比例
       * 填至当前、右侧留白至日终，完整历史日期则恰好铺满。
       */
      fillToClose(line, lastMinOfDay(line, base, 0, market), base, 0, market);
      (main as ISeriesApi<'Line'>).setData(
        line.map((d) => (
          Number.isFinite(d.value)
            ? { time: d.key as UTCTimestamp, value: d.value }
            : { time: d.key as UTCTimestamp } // whitespace（无 value）
        )),
      );

      if (avg) {
        // 腾讯分时的 cumVolume 单位是「手」，与 cumAmount(元) 不同量纲，
        // 均价无法可靠换算 → 退化为价格线（见 indicators.averageLine 注释）
        const av = averageLine(pts, { volumeUnit: 'lot' });
        const avgData = dedupeByTime(
          av.map((v, i) => ({ key: axisTime(base, 0, pts[i]!.time, market), value: v })),
        );
        (avg as ISeriesApi<'Line'>).setData(
          avgData.map((d) => ({ time: d.key as UTCTimestamp, value: d.value })),
        );
      }
      // 视窗 = 全日会话(09:30 → 收盘),锚点保证两端坐标可解析,可精确贴边。
      // 仅日期变化时设定一次;autoScroll 已关,盘中轮询不会顶掉该视窗。
      if (min1RangeKeyRef.current !== date) {
        min1RangeKeyRef.current = date;
        chart.timeScale().setVisibleRange({
          from: base as UTCTimestamp,
          // 右端多留 8 分钟:url式贴边会把 15:00 标签挤出屏(实测仍差 ~14px)
          to: (axisTime(base, 0, sessionCloseHHMM(market), market) + 8 * 60) as UTCTimestamp,
        });
      }

      // 昨收基准线：分时的涨跌参照系（先移除旧的，避免跨指数累积）
      const priceLineHost = main as ISeriesApi<'Line'>;
      if (prevCloseLineRef.current) {
        priceLineHost.removePriceLine(prevCloseLineRef.current);
        prevCloseLineRef.current = null;
      }
      if (prevClose !== null && prevClose !== undefined) {
        prevCloseLineRef.current = priceLineHost.createPriceLine({
          price: prevClose, color: t.prevClose, lineWidth: 1, lineStyle: 2,
          axisLabelVisible: true, title: '昨收',
        });

        /**
         * 分时纵轴必须**围绕昨收对称**，否则曲线会被压成一条直线：
         * autoScale 会把 [0, max] 拿来适配 ~3840 的价格，日内 0.5% 的波动
         * 只占千分之几，肉眼完全看不出形状。
         * 半幅取「实际波动」与 1.2% 中较大者：波动大时不会被截断，
         * 波动小时又不会被过度放大。
         */
        const maxDev = pts.length > 0
          ? Math.max(...pts.map((p) => Math.abs(p.price - prevClose)))
          : 0;
        const halfSpan = Math.max(
          prevClose * (minuteSpanPct ?? 0.012),
          maxDev * 1.25,
        );
        if (halfSpan > 0) {
          setAutoscale(priceLineHost, prevClose - halfSpan, prevClose + halfSpan);
        }
      }
    } else {
      if (bars.length === 0) return;
      const data = bars.map((b) => ({
        time: b.date as Time,
        open: b.open, high: b.high, low: b.low, close: b.close,
      }));
      (main as ISeriesApi<'Candlestick'>).setData(
        data as { time: Time; open: number; high: number; low: number; close: number }[],
      );
    }
  }, [bars, minute, minuteDays, theme, period, prevClose, market]);

  /**
   * 选中柱标记（需求「十字光标选中某根 K 线时查看当日笔记与新闻」）。
   *
   * 改前只有一条十字线，看不出"当前选中的是哪一根" —— 竖线随鼠标移动，
   * 与"已选中"是两回事，用户无法确认笔记面板对应的是哪一天。
   * 用 createSeriesMarkers 在柱下方打一个强调色圆点，作为持久选中态。
   *
   * 「有日记」的标记**不在这里** —— 已改为自绘的浮动圆点（见 noteLane.tsx）：
   * 库只能打在 K 线下方（belowBar 偏移不可调），贴得太近且颜色突兀。
   */
  useEffect(() => {
    const chart = chartRef.current;
    const main = seriesRef.current[0];
    if (!chart || !main || period === 'MIN1' || period === 'MIN5' || bars.length === 0) {
      markersRef.current?.setMarkers([]);
      return;
    }
    const markers: SeriesMarker<Time>[] = [];
    // 库不读 CSS 变量，只接受具体色值；用主题的 text1（高对比）作强调色
    const markerAccent = getChartTheme(theme).text1;

    if (selectedDate && bars.some((b) => b.date === selectedDate)) {
      markers.push({
        time: selectedDate as Time,
        position: 'belowBar',
        color: markerAccent,
        shape: 'circle',
        text: '当前',
      });
    }

    if (!markersRef.current) {
      markersRef.current = createSeriesMarkers(main as ISeriesApi<'Candlestick'>);
    }
    markersRef.current.setMarkers(markers);
  }, [bars, selectedDate, theme, period]);

  /* ── 主图叠加 ── */
  useEffect(() => {
    const chart = chartRef.current;
    const main = seriesRef.current[0];
    const isMin = period === 'MIN1' || period === 'MIN5';
    // 分时无叠加；无数据时保留上一轮（不拆除，见 overlaySeriesRef 的说明）
    if (!chart || !main || isMin || bars.length === 0) return;

    // 有数据 → 重建：先移除上一轮
    for (const s of overlaySeriesRef.current) {
      try { chart.removeSeries(s as never); } catch { /* 图表已销毁 */ }
    }
    overlaySeriesRef.current = [];

    const t = getChartTheme(theme);
    const closes = bars.map((b) => b.close);
    const times = bars.map((b) => b.date as Time);

    if (overlays.has('ma')) {
      // 按用户选中的周期画，而不是硬编码全部 5 条
      for (const n of maPeriods) {
        if (closes.length < n) continue;
        const s = chart.addSeries(LineSeries, {
          color: t.ma[n], lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
          crosshairMarkerVisible: false,
        });
        s.setData(sma(closes, n)
          .map((v, i) => (v === null ? null : { time: times[i]!, value: v }))
          .filter((x): x is { time: Time; value: number } => x !== null));
        overlaySeriesRef.current.push(s);
      }
    }
    if (overlays.has('boll')) {
      const bb = bollinger(closes);
      for (const [series, color] of [[bb.upper, t.boll], [bb.mid, t.boll], [bb.lower, t.boll]] as const) {
        const s = chart.addSeries(LineSeries, {
          color, lineWidth: 1, lineStyle: 2, priceLineVisible: false,
          lastValueVisible: false, crosshairMarkerVisible: false,
        });
        s.setData(series.map((v, i) => (v === null ? null : { time: times[i]!, value: v }))
          .filter((x): x is { time: Time; value: number } => x !== null));
        overlaySeriesRef.current.push(s);
      }
    }
    /**
     * 估值叠加（独立右轴）：
     * 中证只有 20 个交易日数据，若与价格共轴，PE≈15 会被 1500 的价格压成一条直线。
     * 故用 priceScaleId: '' + 独立 margin 挂到右侧。
     */
    if (overlays.has('valuation') && valuation && valuation.length > 0) {
      // PE：pe1=静态（总股本），pe2=TTM（计算用股本）。口径由用户切换。
      if (valuationKind !== 'dividend') {
        const s = chart.addSeries(LineSeries, {
          color: t.pe, lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
          // 独立右轴：PE≈15 与价格 1500 共轴会压成直线
          priceScaleId: '',
          title: valuationBasis === 'pe1' ? 'PE(静)' : 'PE(TTM)',
        });
        s.setData(
          valuation
            .map((d) => {
              const v = valuationBasis === 'pe1' ? d.pe1 : d.pe2;
              return v === undefined ? null : { time: d.date as Time, value: v };
            })
            .filter((x): x is { time: Time; value: number } => x !== null),
        );
        overlaySeriesRef.current.push(s);
      }
      // 股息率（%），同用右轴但量级差异大，仍独立刻度
      if (valuationKind !== 'pe') {
        const s = chart.addSeries(LineSeries, {
          color: t.dividend, lineWidth: 1, lineStyle: 2, priceLineVisible: false,
          lastValueVisible: false, priceScaleId: '', title: '股息率%',
        });
        s.setData(
          valuation
            .map((d) => (d.divYield2 === undefined ? null : { time: d.date as Time, value: d.divYield2 }))
            .filter((x): x is { time: Time; value: number } => x !== null),
        );
        overlaySeriesRef.current.push(s);
      }
    }

    if (overlays.has('bbi')) {
      const s = chart.addSeries(LineSeries, {
        color: t.bbi, lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
      });
      s.setData(bbi(closes).map((v, i) => (v === null ? null : { time: times[i]!, value: v }))
        .filter((x): x is { time: Time; value: number } => x !== null));
      overlaySeriesRef.current.push(s);
    }

  }, [bars, overlays, maPeriods, theme, period]);

  /* ── 副图（v5 多 pane） ── */
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const active = panes.filter((p): p is PaneId => p !== null);
    const isMin = period === 'MIN1' || period === 'MIN5';

    if (isMin) {
      /**
       * 分时副图：**仅 VOL**（分钟后线级成交量）。ChartZone 已把槽位限制为
       * VOL/null —— MACD/RSI/KDJ 在分钟序列上没有与日K一致的口径，不提供。
       *
       * 分钟成交量 = 累计量的逐点差分（CumVolume 是自开盘起累计，单位「手」）：
       *   第一点相对 0，其后 vol[i] - vol[i-1]，负差（上游乱序）钳 0。
       * 5 日分时把各日差分合并到同一 rootpane 序列（约 1200 根），
       * 时间沿用与主价带相同的合成轴 —— 数据域的上限由主价带的收盘
       * whitespace 锚点撑到收盘，VOL 序列无需自带锚点。
       * 柱色按「该分钟价 vs 前一分」的涨跌（与日K VOL 语义一致）。
       */
      // 重新配置槽位 → 先清掉旧序列
      for (const { series } of paneSeriesRef.current) {
        try { chart.removeSeries(series as never); } catch { /* ignore */ }
      }
      paneSeriesRef.current = [];
      if (!active.includes('VOL')) return;

      const t = getChartTheme(theme);
      const build = (
        pts: { time: string; price: number; cumVolume: number }[],
        dayIndex: number,
      ): { time: Time; value: number; color: string }[] => {
        const base = axisBase(axisDates[0]!);
        const out: { time: Time; value: number; color: string }[] = [];
        let prevVol = 0;
        let prevPrice = pts[0]?.price ?? 0;
        for (const p of pts) {
          const vol = Math.max(0, p.cumVolume - prevVol);
          out.push({
            time: axisTime(base, dayIndex, p.time, market) as Time,
            value: vol,
            color: p.price >= prevPrice ? t.volumeUp : t.volumeDown,
          });
          prevVol = p.cumVolume;
          prevPrice = p.price;
        }
        return out;
      };
      const s = chart.addSeries(HistogramSeries, {
        priceFormat: { type: 'volume' },
        priceLineVisible: false, lastValueVisible: false,
      }, 1);
      if (period === 'MIN1') {
        if ((minute?.points ?? []).length === 0) return; // 无数据保留上一轮
        s.setData(build(minute!.points, 0));
      } else {
        const valid = (minuteDays ?? []).filter((d) => d.points.length > 0);
        if (valid.length === 0) return;
        s.setData(valid.flatMap((d, i) => build(d.points, i)));
      }
      paneSeriesRef.current.push({ pane: 1, series: s });
      return;
    }
    /**
     * ⚠️ 无数据时**保留上一轮副图**，不拆除。
     *    若在这里先清空再 return，加载期间副图会消失而主图仍留着旧蜡烛
     *    —— 正是「主副图内容时有时无」的来源。与主图保持一致地保留旧数据。
     */
    if (bars.length === 0) return;

    // 有数据 → 重建：先移除上一轮
    for (const { series } of paneSeriesRef.current) {
      try { chart.removeSeries(series as never); } catch { /* ignore */ }
    }
    paneSeriesRef.current = [];

    const t = getChartTheme(theme);

    const times = bars.map((b) => b.date as Time);
    const closes = bars.map((b) => b.close);

    active.forEach((pane, i) => {
      const paneIndex = i + 1;   // pane 0 为主图
      const created: AnySeries[] = [];

      if (pane === 'VOL') {
        const s = chart.addSeries(HistogramSeries, {
          priceFormat: { type: 'volume' },
          priceLineVisible: false, lastValueVisible: false,
        }, paneIndex);
        s.setData(bars.map((b) => ({
          time: b.date as Time,
          value: b.volume,
          color: b.close >= b.open ? t.volumeUp : t.volumeDown,
        })));
        for (const n of [5, 10] as const) {
          if (closes.length < n) continue;
          const m = chart.addSeries(LineSeries, {
            color: t.ma[n], lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
          }, paneIndex);
          m.setData(volMa(bars.map((b) => b.volume), n)
            .map((v, idx) => (v === null ? null : { time: times[idx]!, value: v }))
            .filter((x): x is { time: Time; value: number } => x !== null));
          created.push(m);
        }
        created.unshift(s);
      } else if (pane === 'MACD') {
        const m = macd(closes);
        const hist = chart.addSeries(HistogramSeries, { priceLineVisible: false, lastValueVisible: false }, paneIndex);
        hist.setData(m.hist.map((v, idx) => ({
          time: times[idx]!,
          value: v ?? 0,
          color: (v ?? 0) >= 0 ? t.up : t.down,
        })));
        for (const [data, color] of [[m.dif, t.ma[5]], [m.dea, t.ma[10]]] as const) {
          const s = chart.addSeries(LineSeries, {
            color, lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
          }, paneIndex);
          s.setData(data.map((v, idx) => (v === null ? null : { time: times[idx]!, value: v }))
            .filter((x): x is { time: Time; value: number } => x !== null));
          created.push(s);
        }
        created.unshift(hist);
      } else if (pane === 'RSI') {
        const s = chart.addSeries(LineSeries, {
          color: t.ma[20], lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
        }, paneIndex);
        const r = rsi(closes);
        s.setData(r.map((v, idx) => (v === null ? null : { time: times[idx]!, value: v }))
          .filter((x): x is { time: Time; value: number } => x !== null));
        // 30/70 超买超卖参考线
        s.createPriceLine({ price: 70, color: t.down, lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: '70' });
        s.createPriceLine({ price: 30, color: t.up, lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: '30' });
        created.push(s);
      } else if (pane === 'KDJ') {
        const kd = kdj(bars);
        for (const [data, color] of [[kd.k, t.ma[5]], [kd.d, t.ma[10]], [kd.j, t.ma[20]]] as const) {
          const s = chart.addSeries(LineSeries, {
            color, lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
          }, paneIndex);
          s.setData(data.map((v, idx) => (v === null ? null : { time: times[idx]!, value: v }))
            .filter((x): x is { time: Time; value: number } => x !== null));
          created.push(s);
        }
      }

      for (const s of created) paneSeriesRef.current.push({ pane: paneIndex, series: s });
    });
  }, [bars, panes, theme, period, market, minute, minuteDays, axisDates]);

  const isMinute = period === 'MIN1' || period === 'MIN5';

  /*
   * 日记圆点的定位点：把笔记日期归并到所属的那根柱。
   *
   * ⚠️ 必须归并，不能直接把日记日期丢给 timeToCoordinate：时间轴上只存在
   * 柱的时间点，周/月/年柱的 date 是「周期最后交易日」（如 10-09），而日记
   * 记在任意交易日（10-08），后者在周K 上查不到坐标、返回 null，圆点会整个
   * 消失。归并后 09-29 / 09-30 同属「09-30」那根周K，合为一个点。
   *
   * 归并用双指针线性推进，bars 与 noteDates 均已升序，O(n+m)。
   */
  const laneNotes = useMemo<NoteDot[]>(() => {
    if (noteDates.length === 0 || bars.length === 0) return [];
    const out: NoteDot[] = [];
    let ni = 0;
    for (const b of bars) {
      const from = b.barStartDate ?? b.date;
      while (ni < noteDates.length && noteDates[ni]!.date < from) ni++;
      let k = ni;
      let count = 0;
      while (k < noteDates.length && noteDates[k]!.date <= b.date) {
        count += noteDates[k]!.count;
        k++;
      }
      if (k === ni) continue;
      const hit = noteDates.slice(ni, k);
      out.push({
        time: b.date,
        title: `${hit.map((h) => h.date).join('、')} · ${count} 篇`,
        selected: b.date === selectedDate,
      });
      ni = k;
      if (ni >= noteDates.length) break;
    }
    return out;
  }, [bars, noteDates, selectedDate]);

  /**
   * 预测圆点：与笔记同一套归并 —— 周/月/年柱内多天的预测合成一个点，
   * 颜色取**柱内最后一个交易日**的情绪（与「柱时间=最后交易日」的轴语义一致），
   * tooltip 逐日枚举。sentiment 为空的 PredictionDay 上游（ChartZone）已过滤。
   */
  const lanePreds = useMemo<NoteDot[]>(() => {
    if (predDates.length === 0 || bars.length === 0) return [];
    const SENT: Record<'bullish' | 'bearish' | 'neutral', { label: string; color: string }> = {
      bullish: { label: '看多', color: 'var(--up)' },
      bearish: { label: '看空', color: 'var(--down)' },
      neutral: { label: '中性', color: 'var(--flat)' },
    };
    const out: NoteDot[] = [];
    let ni = 0;
    for (const b of bars) {
      const from = b.barStartDate ?? b.date;
      while (ni < predDates.length && predDates[ni]!.date < from) ni++;
      let k = ni;
      while (k < predDates.length && predDates[k]!.date <= b.date) k++;
      if (k === ni) continue;
      const hit = predDates.slice(ni, k);
      const last = hit[hit.length - 1]!;
      out.push({
        time: b.date,
        title: hit.map((h) => `${h.date} ${SENT[h.sentiment].label}`).join('、'),
        color: SENT[last.sentiment].color,
        selected: b.date === selectedDate,
      });
      ni = k;
      if (ni >= predDates.length) break;
    }
    return out;
  }, [bars, predDates, selectedDate]);

  return (
    <div
      style={{ position: 'relative', width: '100%', height }}
      onDoubleClick={onCyclePreset}
      title={onCyclePreset ? '双击切换图表高度档' : undefined}
    >
      <div ref={boxRef} style={{ position: 'absolute', inset: 0 }} />
      {/* 有日记：浮在主图上边界内侧的小圆点。不占高度、不画任何边界线 */}
      {!isMinute && <NoteLane chart={chartApi} notes={laneNotes} />}
      {/* 有预测：同一套归并，在笔记行正下方的第二行；情绪色实心 */}
      {!isMinute && <NoteLane chart={chartApi} notes={lanePreds} row={1} />}
      {/* 缩放/平移：库无内置导航 UI，触屏上滚轮缩放与拖拽平移也不易发现 */}
      <PriceScaleControls chart={chartApi} />
      {isMinute && (
        <MinuteTimeAxis chart={chartApi} market={market} dates={axisDates} multi={period === 'MIN5'} />
      )}
    </div>
  );
}
